import type {
  AircraftCatalog,
  ArrivalInquiry,
  EventContact,
  FlightStatus,
  OperatorClass,
  ProgramMembership,
  ScheduleConfidence,
  Triage,
} from '../engine/types'
import { type Tier, TIER_CATEGORIES, TIER_ORDER } from '../engine/tiers'
import { WINDOW_START } from './eventWindow'

/** Deterministic PRNG (mulberry32) so the demo dataset is reproducible. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Masters surge fleet mix (sums to 100). The editable default — override via SyntheticOptions.tierWeights. */
export const DEFAULT_TIER_WEIGHTS: Record<Tier, number> = {
  heavy: 12,
  super_mid: 20,
  mid: 24,
  light: 26,
  turboprop: 12,
  piston: 6,
}

/** Arrival day weights across Apr 2–13 (index = days after WINDOW_START). */
export const DEFAULT_ARRIVAL_DAY_WEIGHTS = [1, 3, 5, 8, 12, 18, 20, 12, 8, 2, 1, 1]
/** Sunday-dominated departure-day weights (Apr 10–13 = day index 8–11). */
export const DEFAULT_DEP_DAY_WEIGHTS: Array<[number, number]> = [
  [8, 5], // Sat Apr 10
  [9, 60], // Sun Apr 11 — the exodus
  [10, 18], // Mon Apr 12
  [11, 9], // Tue Apr 13
]

/** Default deterministic seed (Masters 2027 opening round). */
export const DEFAULT_SEED = 20270408

const FRACTIONALS = [
  'NetJets', 'Flexjet', 'Wheels Up', 'VistaJet', 'Jet Linx',
  'PlaneSense', 'AirSprint', 'Executive Jet Management',
]
const MGMT = [
  'Clay Lacy Aviation', 'Solairus Aviation', 'Jet Aviation', 'Gama Aviation',
  'Priester Aviation', 'Meridian', 'Pentastar Aviation',
]
const INDIVIDUALS = [
  'Anderson Holdings', 'Beacon Capital', 'Coastal Partners', 'Drayton Family Office',
  'Evergreen LLC', 'Fairway Ventures', 'Greenbrier Group', 'Harborview Trust',
]

const SERVICE_TYPES = ['fuel', 'gpu', 'lav', 'catering', 'rental_car', 'hangar_request']

/** Feeder airports the surge traffic originates from (cosmetic, for the header). */
const ORIGIN_ICAOS = [
  'KTEB', 'KHPN', 'KPDK', 'KDAL', 'KAPA', 'KVNY', 'KBED', 'KSDL', 'KFXE', 'KCRG',
]

const CONTACT_FIRST = [
  'James', 'Sarah', 'Michael', 'Emily', 'David', 'Jessica', 'Robert', 'Ashley',
  'William', 'Amanda', 'Thomas', 'Rachel', 'Daniel', 'Laura', 'Brian', 'Megan',
]
const CONTACT_LAST = [
  'Mitchell', 'Carter', 'Bennett', 'Foster', 'Hayes', 'Reed', 'Sullivan', 'Brooks',
  'Coleman', 'Patterson', 'Russell', 'Griffin', 'Webb', 'Hughes', 'Powell', 'Barrett',
]
const CONTACT_ROLES: EventContact['role'][] = ['Primary', 'Ops', 'Billing']

/** Slug an operator name into an email-domain-ish token. */
function operatorSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '') || 'operator'
}

/** 1–3 synthetic contacts for the operator, always including a Primary. */
function makeContacts(rand: () => number, operatorName: string): EventContact[] {
  const domain = `${operatorSlug(operatorName)}.com`
  const n = 1 + Math.floor(rand() * 3)
  const out: EventContact[] = []
  for (let i = 0; i < n; i++) {
    const first = CONTACT_FIRST[Math.floor(rand() * CONTACT_FIRST.length)]
    const last = CONTACT_LAST[Math.floor(rand() * CONTACT_LAST.length)]
    const role = CONTACT_ROLES[i] ?? 'Ops'
    const area = 200 + Math.floor(rand() * 700)
    const mid = 100 + Math.floor(rand() * 900)
    const line = 1000 + Math.floor(rand() * 9000)
    out.push({
      name: `${first} ${last}`,
      role,
      email: `${first.toLowerCase()}.${last.toLowerCase()}@${domain}`,
      phone: `(${area}) ${mid}-${line}`,
    })
  }
  return out
}

/**
 * Seeded membership/program per operator class. Fractionals run on contract-fuel
 * deals; management companies are a mix; individuals are mostly walk-up transient
 * with the occasional loyalty member and rare based tenant (a surge is transient).
 */
function pickProgram(rand: () => number, operatorClass: OperatorClass): ProgramMembership {
  const weights: Array<[ProgramMembership, number]> =
    operatorClass === 'fractional'
      ? [['contract_fuel', 60], ['loyalty', 25], ['transient', 15]]
      : operatorClass === 'management'
        ? [['contract_fuel', 35], ['loyalty', 30], ['transient', 32], ['based_tenant', 3]]
        : [['transient', 64], ['loyalty', 28], ['based_tenant', 8]]
  return pickWeighted(rand, weights)
}

function pickWeighted<T>(rand: () => number, entries: Array<[T, number]>): T {
  const total = entries.reduce((s, [, w]) => s + w, 0)
  let r = rand() * total
  for (const [v, w] of entries) {
    r -= w
    if (r <= 0) return v
  }
  return entries[entries.length - 1][0]
}

function makeTail(rand: () => number): string {
  const digits = 1 + Math.floor(rand() * 4)
  let s = 'N'
  for (let i = 0; i < digits; i++) s += Math.floor(rand() * 10)
  if (rand() < 0.4) {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
    s += letters[Math.floor(rand() * letters.length)]
    if (rand() < 0.4) s += letters[Math.floor(rand() * letters.length)]
  }
  return s
}

/** Build typeCode buckets per tier from the live catalog. */
function buildTierBuckets(catalog: AircraftCatalog): Record<Tier, string[]> {
  const buckets: Record<Tier, string[]> = {
    heavy: [], super_mid: [], mid: [], light: [], turboprop: [], piston: [],
  }
  for (const [tier, cats] of Object.entries(TIER_CATEGORIES) as Array<[Tier, string[]]>) {
    for (const entry of catalog.values()) {
      if (entry.category && cats.includes(entry.category)) buckets[tier].push(entry.typeCode)
    }
  }
  return buckets
}

function pickTypeCode(rand: () => number, tier: Tier, buckets: Record<Tier, string[]>): string | null {
  if (buckets[tier].length > 0) {
    return buckets[tier][Math.floor(rand() * buckets[tier].length)]
  }
  // Fall back to the nearest non-empty tier.
  const idx = TIER_ORDER.indexOf(tier)
  for (let d = 1; d < TIER_ORDER.length; d++) {
    for (const alt of [TIER_ORDER[idx - d], TIER_ORDER[idx + d]]) {
      if (alt && buckets[alt].length > 0) {
        return buckets[alt][Math.floor(rand() * buckets[alt].length)]
      }
    }
  }
  return null
}

function dayTimeHour(rand: () => number): number {
  // Daytime skew 8:00–19:00.
  return 8 + Math.floor(rand() * 11)
}

export interface SyntheticOptions {
  count?: number
  seed?: number
  /** Override the per-tier fleet mix (relative weights — need not sum to 100). */
  tierWeights?: Record<Tier, number>
  /** Override the arrival-day weights (index = days after WINDOW_START). */
  arrivalDayWeights?: number[]
  /** Override the departure-day weights as [dayIndex, weight] pairs. */
  depDayWeights?: Array<[number, number]>
}

/**
 * Generate the demo inquiry set, mirroring (simplified) the server-side
 * `simulator:generate-masters-event` distributions: tier mix, back-loaded
 * arrival ramp, Sunday departure wall, 80/15/5 triage, ~40% services.
 */
export function generateSyntheticInquiries(
  catalog: AircraftCatalog,
  {
    count = 400,
    seed = DEFAULT_SEED,
    tierWeights = DEFAULT_TIER_WEIGHTS,
    arrivalDayWeights = DEFAULT_ARRIVAL_DAY_WEIGHTS,
    depDayWeights = DEFAULT_DEP_DAY_WEIGHTS,
  }: SyntheticOptions = {}
): ArrivalInquiry[] {
  const rand = mulberry32(seed)
  const buckets = buildTierBuckets(catalog)
  const inquiries: ArrivalInquiry[] = []

  // Rebuild the weighted-tier entries in display order from the (possibly
  // overridden) mix record so the picker stays deterministic.
  const tierWeightEntries: Array<[Tier, number]> = TIER_ORDER.map((t) => [t, tierWeights[t] ?? 0])

  for (let i = 0; i < count; i++) {
    const tier = pickWeighted(rand, tierWeightEntries)
    const typeCode = pickTypeCode(rand, tier, buckets)
    if (!typeCode) continue

    // Arrival
    const arrDayIdx = pickWeighted(
      rand,
      arrivalDayWeights.map((w, idx) => [idx, w] as [number, number])
    )
    const arrHour = dayTimeHour(rand)
    const arrMin = Math.floor(rand() * 4) * 15
    const arrival = WINDOW_START + arrDayIdx * DAY_MS + (arrHour * 60 + arrMin) * 60 * 1000

    // Departure: 30% same-day quick turn, else the Sunday-dominated wall.
    let departure: number
    if (rand() < 0.3) {
      const dwellHrs = 2 + Math.floor(rand() * 5)
      departure = arrival + dwellHrs * 60 * 60 * 1000
    } else {
      let depDayIdx = pickWeighted(rand, depDayWeights)
      if (depDayIdx <= arrDayIdx) depDayIdx = arrDayIdx + 1
      const depHour = 7 + Math.floor(rand() * 10) // morning→afternoon exodus
      departure = WINDOW_START + depDayIdx * DAY_MS + depHour * 60 * 60 * 1000
    }

    // Triage 80/15/5
    const tRoll = rand()
    const triage: Triage = tRoll < 0.8 ? 'green' : tRoll < 0.95 ? 'amber' : 'red'

    // Operator class + name. Fractionals dominate Masters traffic (50%), then
    // management companies (25%), then individual owners / family offices (25%).
    const opRoll = rand()
    const operatorClass: OperatorClass =
      opRoll < 0.5 ? 'fractional' : opRoll < 0.75 ? 'management' : 'individual'
    const operatorName =
      operatorClass === 'fractional'
        ? FRACTIONALS[Math.floor(rand() * FRACTIONALS.length)]
        : operatorClass === 'management'
          ? MGMT[Math.floor(rand() * MGMT.length)]
          : INDIVIDUALS[Math.floor(rand() * INDIVIDUALS.length)]
    const program = pickProgram(rand, operatorClass)
    const isVip = rand() < 0.05

    // Services ~40%
    const services: ArrivalInquiry['services'] = []
    if (rand() < 0.4) {
      const nSvc = 1 + Math.floor(rand() * 3)
      const pool = [...SERVICE_TYPES]
      for (let s = 0; s < nSvc && pool.length; s++) {
        const type = pool.splice(Math.floor(rand() * pool.length), 1)[0]
        if (type === 'fuel') {
          // Piston aircraft burn avgas (100LL); everything else takes Jet-A. This
          // gives the fuel-truck sim two visible liveries without changing demand.
          const fuel = tier === 'piston' ? 'avgas' : 'jet_a'
          services.push({ type, gallons: 200 + Math.floor(rand() * 1800), fuel })
        } else {
          services.push({ type })
        }
      }
    }

    // Estimated times — simulate the ETA/ETD a flight-tracking feed would push.
    // Offset is biased toward delays (−5..+90 min) so the demo shows mostly
    // on-time/late traffic with the occasional early bird.
    const MIN_MS = 60 * 1000
    const arrOffsetMin = Math.round(-5 + rand() * 95)
    const depOffsetMin = Math.round(-5 + rand() * 95)
    const estimatedArrival = arrival + arrOffsetMin * MIN_MS
    const estimatedDeparture = Math.max(estimatedArrival + MIN_MS, departure + depOffsetMin * MIN_MS)
    const arrivalStatus: FlightStatus =
      arrOffsetMin > 10 ? 'delayed' : arrOffsetMin < -5 ? 'early' : 'on_time'

    // Confidence: confirmed ~60%, estimated ~30%, predicted ~10%.
    const cRoll = rand()
    const confidence: ScheduleConfidence =
      cRoll < 0.6 ? 'confirmed' : cRoll < 0.9 ? 'estimated' : 'predicted'

    // Callsign: operator initials (or a generic N-reg style) + 1–4 digits.
    const opInitials = operatorName.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase()
    const callsign = `${opInitials || 'EXJ'}${100 + Math.floor(rand() * 900)}`
    const originIcao = ORIGIN_ICAOS[Math.floor(rand() * ORIGIN_ICAOS.length)]

    inquiries.push({
      scheduleId: `syn-${seed}-${i}`,
      aircraftId: `syn-ac-${seed}-${i}`,
      tailNumber: makeTail(rand),
      typeCode,
      operatorName,
      arrival,
      departure,
      estimatedArrival,
      estimatedDeparture,
      arrivalStatus,
      confidence,
      callsign,
      originIcao,
      triage,
      services,
      isVip,
      operatorClass,
      program,
      contacts: makeContacts(rand, operatorName),
    })
  }

  return inquiries
}
